import React, { useEffect, useRef, useState } from "react";
import { Grid, TextField, Autocomplete, Box, IconButton, Typography } from "@mui/material";
import { State } from "country-state-city";
import {
  employmentTypes,
  designations,
  departments,
  sectors,
  companyNames,
  searchCompaniesFromClearbit,
} from "../../data/workData";
import { fetchStates, fetchCities } from "../../data/educationData";
import countriesData from "../../data/countries";
import { fetchPincodeData } from "../../services/pincodeService";
import Icon from "@mui/material/Icon";
import MDButton from "components/MDButton";
import { extractAiTextFromImage } from "../../services/aiOcrService";
import { parseKeyValueText, firstFrom, pickDate } from "../../services/ocrUtils";

const WorkFields = ({
  formData,
  handleChange,
  countries = [],
  states = [],
  cities = [],
  section = 1,
  textInputStyle = {},
  fieldErrors = {},
}) => {
  const [stateOptions, setStateOptions] = useState([]);
  const [stateLoading, setStateLoading] = useState(false);
  const [cityOptions, setCityOptions] = useState([]);
  const [cityLoading, setCityLoading] = useState(false);
  const [companyOptions, setCompanyOptions] = useState(companyNames);
  const [companyLoading, setCompanyLoading] = useState(false);
  const previousStateRef = useRef();

  const companyNameError = fieldErrors[`companyname${section}`] || "";
  const designationError = fieldErrors[`designation${section}`] || "";
  const employmentTypeError = fieldErrors[`employmenttype${section}`] || "";
  const startDateError = fieldErrors[`startdate${section}`] || "";

  // Fetch company names from API when user types
  const handleCompanySearch = async (query) => {
    if (!query || query.trim().length < 2) {
      setCompanyOptions(companyNames);
      return;
    }

    try {
      setCompanyLoading(true);

      // Try to fetch from Clearbit API
      const apiResults = await searchCompaniesFromClearbit(query);

      // Filter static list as fallback
      const filteredStatic = companyNames.filter((company) =>
        company.toLowerCase().includes(query.toLowerCase())
      );

      // Combine API results with filtered static list (remove duplicates)
      const combined = [...new Set([...apiResults, ...filteredStatic])];

      setCompanyOptions(combined.length > 0 ? combined : companyNames);
    } catch (error) {
      console.error("Error searching companies:", error);
      // Fallback to filtered static list
      const filteredStatic = companyNames.filter((company) =>
        company.toLowerCase().includes(query.toLowerCase())
      );
      setCompanyOptions(filteredStatic.length > 0 ? filteredStatic : companyNames);
    } finally {
      setCompanyLoading(false);
    }
  };

  // Fetch states when country changes
  useEffect(() => {
    const countryName = formData[`country${section}`];
    if (!countryName) {
      setStateOptions([]);
      return;
    }

    let cancelled = false;
    const loadStates = async () => {
      try {
        setStateLoading(true);
        let options = [];

        // First attempt: use country-state-city library if ISO code available
        let isoCode = "";
        const providedCountry =
          countries && countries.length > 0 ? countries.find((c) => c.name === countryName) : null;
        if (providedCountry && providedCountry.code) {
          isoCode = providedCountry.code.toUpperCase();
        }
        if (!isoCode) {
          const fallbackCountry = countriesData.find((c) => c.name === countryName);
          if (fallbackCountry && fallbackCountry.code) {
            isoCode = fallbackCountry.code.toUpperCase();
          }
        }

        if (isoCode) {
          const stateData = State.getStatesOfCountry(isoCode);
          if (stateData && stateData.length > 0) {
            options = stateData.map((s) => s.name);
          }
        }

        // Second attempt: use provided states prop if it matches India (legacy support)
        if (!options.length && states && states.length) {
          options = states.map((s) => (typeof s === "string" ? s : s.name || "")).filter(Boolean);
        }

        // Final fallback: call API helper
        if (!options.length) {
          const apiStates = await fetchStates(countryName);
          if (apiStates && apiStates.length) {
            options = apiStates;
          }
        }

        if (!cancelled) {
          setStateOptions(options);
        }
      } catch (error) {
        if (!cancelled) {
          setStateOptions([]);
        }
      } finally {
        if (!cancelled) {
          setStateLoading(false);
        }
      }
    };

    loadStates();

    return () => {
      cancelled = true;
    };
  }, [formData[`country${section}`]]);

  // Fetch cities whenever country/state changes
  useEffect(() => {
    const countryName = formData[`country${section}`];
    const stateName = formData[`state${section}`];

    if (!countryName) {
      setCityOptions([]);
      return;
    }

    let cancelled = false;
    const loadCities = async () => {
      try {
        setCityLoading(true);
        let options = [];

        const fetched = await fetchCities(countryName, stateName);
        if (fetched && fetched.length) {
          options = fetched;
        }

        if (!options.length && cities && cities.length) {
          options = cities.map((c) => (typeof c === "string" ? c : c.name || "")).filter(Boolean);
        }

        if (!cancelled) {
          const deduped = Array.from(new Set(options))
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));
          setCityOptions(deduped);
        }
      } catch (error) {
        if (!cancelled) {
          const fallback =
            cities && cities.length
              ? cities.map((c) => (typeof c === "string" ? c : c.name || "")).filter(Boolean)
              : [];
          const dedupedFallback = Array.from(new Set(fallback))
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));
          setCityOptions(dedupedFallback);
        }
      } finally {
        if (!cancelled) {
          setCityLoading(false);
        }
      }
    };

    loadCities();

    return () => {
      cancelled = true;
    };
  }, [formData[`country${section}`], formData[`state${section}`], cities]);

  // Clear city value if state changes and existing city is not in filtered list
  useEffect(() => {
    const currentState = formData[`state${section}`] || "";
    const currentCity = formData[`city${section}`] || "";

    if (previousStateRef.current !== currentState) {
      previousStateRef.current = currentState;

      if (currentCity && cityOptions.length && !cityOptions.includes(currentCity)) {
        handleChange({ target: { name: `city${section}`, value: "" } });
      }
    }
  }, [cityOptions, formData[`state${section}`], formData[`city${section}`]]);

  // On pincode select or blur, fill city/state
  const handlePincodeSelectOrBlur = async () => {
    const countryName = formData[`country${section}`];
    const pincode = formData[`pincode${section}`];

    if (!countryName || !pincode || pincode.trim().length < 3) {
      return;
    }

    let countryCode = "IN";
    const providedCountry =
      countries && countries.length > 0 ? countries.find((c) => c.name === countryName) : null;
    if (providedCountry && providedCountry.code) {
      countryCode = providedCountry.code.toUpperCase();
    } else {
      const fallbackCountry = countriesData.find((c) => c.name === countryName);
      if (fallbackCountry && fallbackCountry.code) {
        countryCode = fallbackCountry.code.toUpperCase();
      }
    }

    const result = await fetchPincodeData(pincode, countryCode || "IN");
    if (result && result.success) {
      if (result.city && formData[`city${section}`] !== result.city) {
        handleChange({ target: { name: `city${section}`, value: result.city } });
      }
      if (result.state && formData[`state${section}`] !== result.state) {
        handleChange({ target: { name: `state${section}`, value: result.state } });
      }
    }
  };

  const [ocrBusy, setOcrBusy] = useState(false);

  return (
    <Grid container spacing={3}>
      {/* Upload Image - moved to top */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">ID Card Image</label>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <input
            accept="image/*"
            style={{ display: "none" }}
            id={`work-image-upload-${section}`}
            type="file"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) {
                const reader = new FileReader();
                reader.onloadend = () => {
                  handleChange({ target: { name: `workimage${section}`, value: reader.result } });
                };
                reader.readAsDataURL(file);
                setOcrBusy(true);
                (async () => {
                  try {
                    const text = await extractAiTextFromImage(file);
                    const kv = parseKeyValueText(text);
                    const updates = {
                      [`companyname${section}`]: firstFrom(kv, ["companyname", "company", "employer", "organization"]),
                      [`designation${section}`]: firstFrom(kv, ["designation", "title", "role", "position"]),
                      [`employmenttype${section}`]: firstFrom(kv, ["employmenttype", "emptype", "type"]),
                      [`startdate${section}`]: pickDate(firstFrom(kv, ["startdate", "joiningdate", "doj"]))
                    };
                    for (const [name, value] of Object.entries(updates)) {
                      if (value) handleChange({ target: { name, value } });
                    }
                  } catch (e) {
                    console.warn("WorkFields OCR failed:", e?.message || e);
                  } finally {
                    setOcrBusy(false);
                  }
                })();
              }
            }}
          />
          <label htmlFor={`work-image-upload-${section}`}>
            <MDButton
              variant="outlined"
              color="info"
              component="span"
              size="small"
              startIcon={<Icon>upload</Icon>}
            >
              Upload Image
            </MDButton>
          </label>
          <Typography variant="caption" color="text.secondary">
            {ocrBusy ? "Reading text to auto-fill…" : "Upload image to auto fill"}
          </Typography>
          {formData[`workimage${section}`] && (
            <Box
              sx={{
                position: "relative",
                mt: 2,
                border: "2px solid #e0e0e0",
                borderRadius: "8px",
                padding: "8px",
                backgroundColor: "#f9f9f9",
              }}
            >
              <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: "block" }}>
                Preview:
              </Typography>
              <Box
                component="img"
                src={formData[`workimage${section}`]}
                alt="ID card preview"
                sx={{
                  width: "100%",
                  maxHeight: 150,
                  objectFit: "contain",
                  borderRadius: "4px",
                  backgroundColor: "white",
                }}
              />
              <IconButton
                size="small"
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  boxShadow: 1,
                  "&:hover": { backgroundColor: "white" },
                }}
                onClick={() => {
                  handleChange({ target: { name: `workimage${section}`, value: "" } });
                  const input = document.getElementById(`work-image-upload-${section}`);
                  if (input) {
                    input.value = "";
                  }
                }}
              >
                <Icon fontSize="small" color="error">close</Icon>
              </IconButton>
            </Box>
          )}
        </Box>
      </Grid>
      {/* Company Name */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
        <Autocomplete
          freeSolo
          options={companyOptions}
          loading={companyLoading}
          value={formData[`companyname${section}`] || ""}
          onChange={(e, value) =>
            handleChange({ target: { name: `companyname${section}`, value } })
          }
          onInputChange={(e, value) => {
            if (e && e.type === "change") {
              handleCompanySearch(value);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Enter or select company name"
              sx={textInputStyle}
              error={!!companyNameError}
              helperText={companyNameError}
            />
          )}
        />
      </Grid>

      {/* Sector */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Sector/Industry</label>
        <Autocomplete
          freeSolo
          options={sectors}
          value={formData[`sector${section}`] || ""}
          onChange={(e, value) => handleChange({ target: { name: `sector${section}`, value } })}
          renderInput={(params) => (
            <TextField {...params} placeholder="Enter or select sector" sx={textInputStyle} />
          )}
        />
      </Grid>

      {/* Department */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
        <Autocomplete
          freeSolo
          options={departments}
          value={formData[`department${section}`] || ""}
          onChange={(e, value) => handleChange({ target: { name: `department${section}`, value } })}
          renderInput={(params) => (
            <TextField {...params} placeholder="Enter or select department" sx={textInputStyle} />
          )}
        />
      </Grid>

      {/* Designation */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
        <Autocomplete
          freeSolo
          options={designations}
          value={formData[`designation${section}`] || ""}
          onChange={(e, value) =>
            handleChange({ target: { name: `designation${section}`, value } })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Enter or select designation"
              sx={textInputStyle}
              error={!!designationError}
              helperText={designationError}
            />
          )}
        />
      </Grid>

      {/* Employment Type */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type</label>
        <Autocomplete
          freeSolo
          options={employmentTypes}
          value={formData[`employmenttype${section}`] || ""}
          onChange={(e, value) =>
            handleChange({ target: { name: `employmenttype${section}`, value } })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select employment type"
              sx={textInputStyle}
              error={!!employmentTypeError}
              helperText={employmentTypeError}
            />
          )}
        />
      </Grid>

      {/* Description */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Job Description / Responsibilities
        </label>
        <TextField
          fullWidth
          name={`description${section}`}
          value={formData[`description${section}`] || ""}
          onChange={handleChange}
          variant="outlined"
          placeholder="Describe your role and responsibilities"
          sx={textInputStyle}
        />
      </Grid>


      {/* Start Date */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
        <TextField
          fullWidth
          type="date"
          name={`startdate${section}`}
          value={formData[`startdate${section}`] || ""}
          onChange={handleChange}
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          sx={textInputStyle}
          error={!!startDateError}
          helperText={startDateError}
        />
      </Grid>

      {/* Currently Working */}
      <Grid item xs={12} md={6} style={{ display: "flex", alignItems: "center" }}>
        <label className="block text-sm font-medium text-gray-700 mb-1" style={{ marginRight: 12 }}>
          Currently Working
        </label>
        <input
          type="checkbox"
          name={`currentlyworking${section}`}
          checked={!!formData[`currentlyworking${section}`]}
          onChange={(e) =>
            handleChange({
              target: { name: `currentlyworking${section}`, value: e.target.checked },
            })
          }
          style={{ width: 20, height: 20 }}
        />
      </Grid>

      {/* End Date */}
      {!formData[`currentlyworking${section}`] && (
        <Grid item xs={12} md={6}>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
          <TextField
            fullWidth
            type="date"
            name={`enddate${section}`}
            value={formData[`enddate${section}`] || ""}
            onChange={handleChange}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            sx={textInputStyle}
          />
        </Grid>
      )}

      {/* Address */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
        <TextField
          fullWidth
          name={`address${section}`}
          value={formData[`address${section}`] || ""}
          onChange={handleChange}
          variant="outlined"
          placeholder="Enter company address"
          sx={textInputStyle}
        />
      </Grid>

      {/* Country */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
        <Autocomplete
          freeSolo
          options={
            countries && countries.length > 0
              ? countries.map((c) => c.name)
              : countriesData.map((c) => c.name)
          }
          value={formData[`country${section}`] || ""}
          onChange={(e, value) => {
            handleChange({ target: { name: `country${section}`, value } });
          }}
          renderInput={(params) => (
            <TextField {...params} placeholder="Enter or select country" sx={textInputStyle} />
          )}
        />
      </Grid>

      {/* State */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">State/Province</label>
        <Autocomplete
          freeSolo
          options={stateOptions}
          loading={stateLoading}
          value={formData[`state${section}`] || ""}
          onChange={(e, value) => handleChange({ target: { name: `state${section}`, value } })}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Enter or select state/province"
              sx={textInputStyle}
            />
          )}
        />
      </Grid>

      {/* City */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
        <Autocomplete
          freeSolo
          options={cityOptions}
          loading={cityLoading}
          value={formData[`city${section}`] || ""}
          onChange={(e, value) => handleChange({ target: { name: `city${section}`, value } })}
          renderInput={(params) => (
            <TextField {...params} placeholder="Enter or select city" sx={textInputStyle} />
          )}
        />
      </Grid>

      {/* Pincode */}
      <Grid item xs={12} md={6}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Pincode/Postal Code</label>
        <TextField
          fullWidth
          name={`pincode${section}`}
          value={formData[`pincode${section}`] || ""}
          onChange={handleChange}
          onBlur={handlePincodeSelectOrBlur}
          variant="outlined"
          placeholder="Enter pincode"
          sx={textInputStyle}
        />
      </Grid>
    </Grid>
  );
};

export default WorkFields;
